// src/components/EventPageEditor.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Topbar from './Topbar';
import Sidebar from './Sidebar';
import Canvas from './Canvas';
import Inspector from './Inspector';
import './EventPageEditor.css';
import useApiContext from 'hooks/useApiContext';

// Import fonts so webpack processes them and they're available
import MarianneRegular from '../../assets/fonts/Marianne-Regular.ttf';
import MarianneMedium from '../../assets/fonts/Marianne-Medium.ttf';
import MarianneBold from '../../assets/fonts/Marianne-Bold.ttf';

const DEFAULT_BLOCKS = [
  {
    id: 'block-1',
    type: 'heading',
    content: 'PROWEIN DÜSSELDORF 2026',
    level: 'h1',
  },
  {
    id: 'block-2',
    type: 'paragraph',
    content:
      "Le salon ProWein Düsseldorf est l'un des rendez-vous majeurs en Europe pour les vins, spiritueux, bières et cidres !",
  },
];

function EventPageEditor({
  eventId,
  eventName,
  organizerName,
  initialBlocks = DEFAULT_BLOCKS,
  onSave = () => {},
  onPublish = () => {},
  apiUrl = '/api',
}) {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [isPublished, setIsPublished] = useState(false);
  const [draggedBlockType, setDraggedBlockType] = useState(null);
  const [draggedBlockId, setDraggedBlockId] = useState(null);
  const [draggedFromSidebar, setDraggedFromSidebar] = useState(false);
  const [notification, setNotification] = useState(null);
  const canvasRef = useRef(null);

  const { eventRepository } = useApiContext();

  // ============================================================================
  // DRAG & DROP
  // ============================================================================

  const handleBlockDragStartSidebar = useCallback((blockType) => {
    setDraggedBlockType(blockType);
    setDraggedFromSidebar(true);
    setDraggedBlockId(null);
  }, []);

  const handleBlockDragStartCanvas = useCallback((blockId) => {
    setDraggedBlockId(blockId);
    setDraggedBlockType(null);
    setDraggedFromSidebar(false);
  }, []);

  const handleCanvasDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = draggedFromSidebar ? 'copy' : 'move';
    },
    [draggedFromSidebar]
  );

  const handleCanvasDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (draggedFromSidebar && draggedBlockType) {
        addBlock(draggedBlockType, e.target);
        showNotification('Bloc ajouté');
      } else if (draggedBlockId && !draggedFromSidebar) {
        const target = e.target.closest('[data-block-id]');
        if (target && target.dataset.blockId !== draggedBlockId) {
          reorderBlocks(draggedBlockId, target.dataset.blockId, e);
          showNotification('Bloc déplacé');
        }
      }

      setDraggedBlockType(null);
      setDraggedBlockId(null);
      setDraggedFromSidebar(false);
    },
    [draggedBlockType, draggedBlockId, draggedFromSidebar, blocks]
  );

  useEffect(() => {
    const handleDragEnd = () => {
      setDraggedBlockType(null);
      setDraggedBlockId(null);
      setDraggedFromSidebar(false);
    };
    window.addEventListener('dragend', handleDragEnd);
    eventRepository.getEventPage(eventId).then((data) => {
      if (data && data.blocks) {
        setBlocks(data.blocks);
      }
    });
    return () => window.removeEventListener('dragend', handleDragEnd);
  }, []);

  // ============================================================================
  // BLOCK MANAGEMENT
  // ============================================================================

  const addBlock = useCallback(
    (type, targetElement = null) => {
      const newBlock = {
        id: `block-${Date.now()}`,
        type,
        ...getDefaultBlockData(type),
      };

      const newBlocks = [...blocks];

      if (targetElement) {
        const target = targetElement.closest('[data-block-id]');
        if (target) {
          const targetIndex = newBlocks.findIndex(
            (b) => b.id === target.dataset.blockId
          );
          if (targetIndex !== -1) {
            // Ajouter le bloc après le bloc cible
            newBlocks.splice(targetIndex + 1, 0, newBlock);
          } else {
            newBlocks.push(newBlock);
          }
        } else {
          newBlocks.push(newBlock);
        }
      } else {
        newBlocks.push(newBlock);
      }

      setBlocks(newBlocks);
      setSelectedBlockId(newBlock.id);
      savePageData(newBlocks);
    },
    [blocks]
  );

  const deleteBlock = useCallback(
    (blockId) => {
      const newBlocks = blocks.filter((b) => b.id !== blockId);
      setBlocks(newBlocks);
      setSelectedBlockId(null);
      showNotification('Bloc supprimé');
      savePageData(newBlocks);
    },
    [blocks]
  );

  const updateBlock = useCallback(
    (blockId, updates) => {
      const newBlocks = blocks.map((b) =>
        b.id === blockId ? { ...b, ...updates } : b
      );
      setBlocks(newBlocks);
      savePageData(newBlocks);
    },
    [blocks]
  );

  const updateBlockArray = useCallback(
    (blockId, field, index, value) => {
      const newBlocks = blocks.map((b) => {
        if (b.id === blockId) {
          const updated = { ...b };
          updated[field][index] = value;
          return updated;
        }
        return b;
      });
      setBlocks(newBlocks);
      savePageData(newBlocks);
    },
    [blocks]
  );

  const reorderBlocks = useCallback(
    (sourceId, targetId, e) => {
      const sourceIndex = blocks.findIndex((b) => b.id === sourceId);
      const targetIndex = blocks.findIndex((b) => b.id === targetId);

      if (sourceIndex !== -1 && targetIndex !== -1) {
        const newBlocks = [...blocks];
        const [moved] = newBlocks.splice(sourceIndex, 1);

        const rect = e.target
          .closest('[data-block-id]')
          .getBoundingClientRect();
        const middle = rect.top + rect.height / 2;

        if (e.clientY < middle) {
          newBlocks.splice(targetIndex, 0, moved);
        } else {
          const newTarget =
            targetIndex > sourceIndex ? targetIndex : targetIndex + 1;
          newBlocks.splice(newTarget, 0, moved);
        }

        setBlocks(newBlocks);
        savePageData(newBlocks);
      }
    },
    [blocks]
  );

  const selectBlock = useCallback((blockId) => {
    setSelectedBlockId(blockId);
  }, []);

  // ============================================================================
  // API CALLS
  // ============================================================================

  const savePageData = useCallback(
    async (blocksToSave) => {
      try {
        const response = await eventRepository.addPage(eventId, {
          blocks: blocksToSave,
        });

        if (response.ok) {
          onSave(blocksToSave);
        }
      } catch (error) {
        console.error('Save error:', error);
      }
    },
    [eventId, apiUrl, onSave]
  );

  const savePage = useCallback(() => {
    savePageData(blocks);
    showNotification('✓ Page enregistrée');
  }, [blocks, savePageData]);

  const publishPage = useCallback(async () => {
    try {
      const response = await eventRepository.publishEventPage(eventId, {
        blocks,
      });
      if (response.ok) {
        setIsPublished(true);
        showNotification('✓ Page publiée');
        onPublish(blocks);
      } else {
        showNotification('Erreur lors de la publication', 'error');
      }
    } catch (error) {
      console.error('Publish error:', error);
      showNotification('Erreur lors de la publication', 'error');
    }
  }, [blocks, eventId, apiUrl, onPublish]);

  const generatePdf = useCallback(async () => {
    try {
      showNotification('Génération du PDF en cours...', 'info');
      const response = await eventRepository.generateEventPagePdf(eventId);

      if (response) {
        // Create blob and download
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `event-${eventId}-page.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        showNotification('PDF généré avec succès');
      } else {
        if (response && response.status === 404) {
          showNotification("Page de l'événement non trouvée (404)", 'error');
        } else {
          console.error('PDF generation error:', response);
          showNotification('Erreur lors de la génération du PDF', 'error');
        }
      }
    } catch (error) {
      if (error && error.status === 404) {
        showNotification("Page de l'événement non trouvée (404)", 'error');
      } else {
        console.error('PDF generation error:', error);
        showNotification('Erreur lors de la génération du PDF', 'error');
      }
    }
  }, [blocks, eventId]);

  const previewPage = useCallback(() => {
    const preview = window.open('', 'preview', 'width=900,height=600');
    const html = generatePreviewHtml(blocks);
    preview.document.write(html);
  }, [blocks]);

  // ============================================================================
  // NOTIFICATIONS & UTILS
  // ============================================================================

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 2000);
  };

  // ============================================================================
  // AUTO-SAVE
  // ============================================================================

  useEffect(() => {
    const interval = setInterval(() => {
      savePageData(blocks);
    }, 30000);
    return () => clearInterval(interval);
  }, [blocks, savePageData]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  return (
    <div className="event-page-editor">
      <Topbar
        eventName={eventName}
        organizerName={organizerName}
        isPublished={isPublished}
        onPreview={previewPage}
        onSave={savePage}
        onPublish={publishPage}
        generatePdf={generatePdf}
      />

      <div className="editor-main">
        <Sidebar onDragStart={handleBlockDragStartSidebar} />

        <Canvas
          blocks={blocks}
          selectedBlockId={selectedBlockId}
          onSelectBlock={selectBlock}
          onDeleteBlock={deleteBlock}
          onDragStart={handleBlockDragStartCanvas}
          onDragOver={handleCanvasDragOver}
          onDrop={handleCanvasDrop}
          canvasRef={canvasRef}
        />

        <Inspector
          block={selectedBlock}
          onUpdate={(updates) =>
            selectedBlock && updateBlock(selectedBlockId, updates)
          }
          onUpdateArray={(field, index, value) =>
            selectedBlock &&
            updateBlockArray(selectedBlockId, field, index, value)
          }
          onDelete={() => selectedBlock && deleteBlock(selectedBlockId)}
        />
      </div>

      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function getDefaultBlockData(type) {
  const defaults = {
    heading: {
      content: 'Nouveau titre',
      level: 'h2',
      fontSize: '32',
      fontFamily: 'Marianne',
      fontWeight: 'bold',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
    },
    paragraph: {
      content: 'Votre texte ici...',
      fontSize: '16',
      fontFamily: 'Marianne',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
    },
    richtext: {
      content: '<p>Texte avec formatage...</p>',
      fontSize: '16',
      fontFamily: 'Marianne',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
    },
    button: { label: 'Cliquez ici', link: '' },
    image: { url: '', alt: '' },
    divider: {},
    features: { items: ['Élément 1', 'Élément 2'] },
    stats: { items: [{ label: 'Chiffre', value: '100' }] },
    download: { title: 'Télécharger', description: '' },
    contact: {
      contacts: [
        {
          name: 'Nom',
          title: 'Titre',
          email: 'email@example.com',
          phone: '+33 1 00 00 00 00',
        },
      ],
    },
  };
  return defaults[type] || {};
}

function generatePreviewHtml(blocks) {
  // Get the base URL for the current application
  const baseUrl = window.location.origin;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Aperçu</title>
      <style>
        @font-face {
          font-family: "Marianne";
          src: url("${baseUrl}${MarianneRegular}") format("truetype");
          font-weight: 400;
          font-style: normal;
        }
        @font-face {
          font-family: "Marianne";
          src: url("${baseUrl}${MarianneMedium}") format("truetype");
          font-weight: 500;
          font-style: normal;
        }
        @font-face {
          font-family: "Marianne";
          src: url("${baseUrl}${MarianneBold}") format("truetype");
          font-weight: 700;
          font-style: normal;
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: "Marianne", Arial, sans-serif; color: #2d2520; line-height: 1.6; }
        .wrapper { max-width: 800px; margin: 0 auto; padding: 40px 20px; }
        h1, h2, h3 { font-family: 'Marianne', Arial, sans-serif; margin: 20px 0 10px; font-weight: bold; }
        h1 { font-size: 2.5rem; }
        h2 { font-size: 2rem; }
        p { margin: 12px 0; color: #666; }
        a { color: #8b0000; }
      </style>
    </head>
    <body>
      <div class="wrapper">
        ${blocks.map((block) => renderBlockPreview(block)).join('')}
      </div>
    </body>
    </html>
  `;
}

function renderBlockPreview(block) {
  // Helper to generate inline styles for text blocks
  const getTextStyles = (block) => {
    const styles = [];
    if (block.fontSize) styles.push(`font-size: ${block.fontSize}px`);
    if (block.fontFamily) {
      // Add fallback fonts for better compatibility
      const fontFamilyWithFallback =
        block.fontFamily === 'Marianne'
          ? '"Marianne", Arial, sans-serif'
          : block.fontFamily === 'Arial'
          ? 'Arial, sans-serif'
          : block.fontFamily === 'Helvetica'
          ? 'Helvetica, Arial, sans-serif'
          : block.fontFamily === 'Georgia'
          ? 'Georgia, serif'
          : block.fontFamily === 'Times New Roman'
          ? '"Times New Roman", Times, serif'
          : block.fontFamily === 'Courier New'
          ? '"Courier New", Courier, monospace'
          : block.fontFamily === 'Verdana'
          ? 'Verdana, sans-serif'
          : block.fontFamily === 'Roboto'
          ? 'Roboto, Arial, sans-serif'
          : `"${block.fontFamily}", sans-serif`;
      styles.push(`font-family: ${fontFamilyWithFallback}`);
    }
    if (block.fontWeight) styles.push(`font-weight: ${block.fontWeight}`);
    if (block.fontStyle) styles.push(`font-style: ${block.fontStyle}`);
    if (block.textDecoration)
      styles.push(`text-decoration: ${block.textDecoration}`);
    if (block.textAlign) styles.push(`text-align: ${block.textAlign}`);
    return styles.length > 0 ? ` style="${styles.join('; ')}"` : '';
  };

  switch (block.type) {
    case 'heading': {
      const HeadingTag = block.level || 'h2';
      return `<${HeadingTag}${getTextStyles(block)}>${
        block.content || ''
      }</${HeadingTag}>`;
    }

    case 'paragraph':
      return `<p${getTextStyles(block)}>${block.content || ''}</p>`;

    case 'richtext':
      return `<div${getTextStyles(block)}>${block.content || ''}</div>`;

    case 'button':
      return `<div style="margin: 20px 0;">
        <a href="${block.link || '#'}" 
           style="display: inline-block; padding: 12px 24px; background: #8b0000; color: white; text-decoration: none; border-radius: 4px;">
          ${block.label || 'Button'}
        </a>
      </div>`;

    case 'image':
      return block.url
        ? `<div style="margin: 20px 0;">
             <img src="${block.url}" alt="${
            block.alt || ''
          }" style="max-width: 100%; height: auto;" />
           </div>`
        : '<div style="padding: 40px; background: #f0f0f0; text-align: center; margin: 20px 0;">Image</div>';

    case 'divider':
      return '<hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />';

    case 'features':
      return `<ul style="list-style: none; padding: 0; margin: 20px 0;">
        ${(block.items || [])
          .map(
            (
              item
            ) => `<li style="padding: 8px 0; padding-left: 24px; position: relative;">
          <span style="position: absolute; left: 0;">✓</span> ${item}
        </li>`
          )
          .join('')}
      </ul>`;

    case 'stats':
      return `<div style="display: flex; gap: 20px; margin: 30px 0; flex-wrap: wrap;">
        ${(block.items || [])
          .map(
            (stat) => `
          <div style="flex: 1; min-width: 150px; text-align: center; padding: 20px; background: #f8f8f8; border-radius: 8px;">
            <div style="font-size: 2rem; font-weight: bold; color: #8b0000;">${
              stat.value || ''
            }</div>
            <div style="margin-top: 8px; color: #666;">${stat.label || ''}</div>
          </div>
        `
          )
          .join('')}
      </div>`;

    case 'download':
      return `<div style="margin: 30px 0; padding: 20px; background: #f8f8f8; border-radius: 8px;">
        <h3 style="margin: 0 0 10px 0;">${block.title || 'Download'}</h3>
        ${
          block.description
            ? `<p style="margin: 0 0 15px 0;">${block.description}</p>`
            : ''
        }
        <a href="#" style="color: #8b0000; text-decoration: none;">📥 Télécharger</a>
      </div>`;

    case 'contact':
      return `<div style="margin: 30px 0;">
        ${(block.contacts || [])
          .map(
            (contact) => `
          <div style="padding: 20px; background: #f8f8f8; border-radius: 8px; margin-bottom: 15px;">
            <h4 style="margin: 0 0 5px 0;">${contact.name || ''}</h4>
            ${
              contact.title
                ? `<p style="margin: 0 0 10px 0; color: #666;">${contact.title}</p>`
                : ''
            }
            ${
              contact.email
                ? `<p style="margin: 5px 0;">📧 <a href="mailto:${contact.email}" style="color: #8b0000;">${contact.email}</a></p>`
                : ''
            }
            ${
              contact.phone
                ? `<p style="margin: 5px 0;">📞 ${contact.phone}</p>`
                : ''
            }
          </div>
        `
          )
          .join('')}
      </div>`;

    default:
      return `<div style="padding: 20px; background: #f0f0f0; margin: 10px 0;">
        Block type: ${block.type}
      </div>`;
  }
}
EventPageEditor.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  eventName: PropTypes.string.isRequired,
  organizerName: PropTypes.string.isRequired,
  initialBlocks: PropTypes.array,
  onSave: PropTypes.func,
  onPublish: PropTypes.func,
  apiUrl: PropTypes.string,
  generatePdf: PropTypes.func,
};

export default EventPageEditor;
